import { Box, Button, Typography } from '@mui/material'
import * as Sentry from '@sentry/react'
import { type ErrorComponentProps, useLocation, useRouter } from '@tanstack/react-router'
import { Component, type ReactNode, useEffect } from 'react'
import { Failure } from '@/components/state/Failure'
import { Loading } from '@/components/state/Loading'
import NiSquircle from '@/ui/icons/nexture/ni-squircle'
import { ButtonLink } from '@/ui/kit/ButtonLink'

/** Início de cada lado. A tela do paciente não aponta para `/app`, e vice-versa. */
function inicioDe(pathname: string): '/app' | '/portal' | '/' {
  if (pathname.startsWith('/app')) return '/app'
  if (pathname.startsWith('/portal')) return '/portal'
  return '/'
}

/**
 * Página inexistente. O texto não diz que tipo de atendimento é: quem cai
 * aqui pode nem ter entrado ainda.
 */
export function RouteNotFound() {
  const { pathname } = useLocation()
  return (
    <Box
      data-slot="nao-encontrado"
      className="flex min-h-[50vh] flex-col items-center justify-center px-6 py-12 text-center"
    >
      <NiSquircle size={28} className="text-text-disabled mb-3" aria-hidden />
      <Typography variant="h6" component="h1" className="text-text-primary">
        Página não encontrada
      </Typography>
      <Typography variant="body2" className="text-text-primary-light mt-1 max-w-sm">
        O endereço pode ter sido digitado errado, ou a página não existe mais.
      </Typography>
      <ButtonLink to={inicioDe(pathname)} variant="contained" className="mt-4">
        Voltar ao início
      </ButtonLink>
    </Box>
  )
}

/**
 * Erro de carregamento ou de desenho de uma rota. Tentar de novo refaz os
 * loaders e remonta; voltar leva ao início do lado certo.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const { pathname } = useLocation()

  useEffect(() => {
    // Sem DSN o Sentry não sobe e isto não faz nada.
    Sentry.captureException(error)
  }, [error])

  return (
    <Failure
      error={error}
      className="min-h-[50vh]"
      onRetry={async () => {
        reset()
        await router.invalidate()
      }}
      action={
        <ButtonLink to={inicioDe(pathname)} variant="text" color="grey">
          Voltar ao início
        </ButtonLink>
      }
    />
  )
}

export function RoutePending() {
  return <Loading label="Abrindo a página" rows={3} />
}

interface AppCrashState {
  error: unknown
}

/**
 * Última rede, fora do roteador: erro de desenho que escapou de tudo. Sem
 * tema nem roteador garantidos, então é HTML simples e recarregar a página.
 */
export class AppCrashBoundary extends Component<{ children: ReactNode }, AppCrashState> {
  override state: AppCrashState = { error: null }

  static getDerivedStateFromError(error: unknown): AppCrashState {
    return { error }
  }

  override componentDidCatch(error: unknown): void {
    Sentry.captureException(error)
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children
    return (
      <div
        role="alert"
        style={{ maxWidth: 420, margin: '15vh auto', padding: 16, textAlign: 'center' }}
      >
        <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Algo deu errado</p>
        <p style={{ margin: '8px 0 16px' }}>
          A página parou de responder. Recarregue para continuar. O que já estava salvo continua
          salvo.
        </p>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Recarregar a página
        </Button>
      </div>
    )
  }
}
